/**
 * Admin View
 * Admin-only panel for system management
 * Tabbed interface: Overview, Analytics Lab, Cache Management, and more
 */

import type { PicksFilters } from '@server/services/admin/adminPicksTypes.ts';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { LabView } from '../lab/LabView';
import { AIAccuracyTab } from './AIAccuracyTab';
import { CacheTab } from './CacheTab';
import { DirectionPriorsTab } from './DirectionPriorsTab';
import { DisputesTab } from './DisputesTab';
import { InjuryComparisonTab } from './InjuryComparisonTab';
import { OverviewTab } from './OverviewTab';
import { PayoutsTab } from './PayoutsTab';
import { PicksDataTab } from './PicksDataTab';
import { SupportTab } from './SupportTab';
import { UserManagementTab } from './UserManagementTab';

// =============================================================================
// Types
// =============================================================================

type AdminTab =
  | 'overview'
  | 'lab'
  | 'cache'
  | 'users'
  | 'payouts'
  | 'disputes'
  | 'support'
  | 'ai-accuracy'
  | 'direction-priors'
  | 'picks-data'
  | 'injury-comparison';

interface AdminTabEntry {
  id: AdminTab;
  label: string;
  description: string;
}

const ADMIN_TABS: AdminTabEntry[] = [
  {
    id: 'overview',
    label: 'Overview',
    description: 'System health & leaderboard management',
  },
  {
    id: 'users',
    label: 'Users',
    description: 'User management, analytics & permissions',
  },
  {
    id: 'payouts',
    label: 'Payouts',
    description: 'Creator payouts & transfers',
  },
  {
    id: 'disputes',
    label: 'Disputes',
    description: 'Chargebacks & dispute management',
  },
  {
    id: 'support',
    label: 'Support',
    description: 'Contact form submissions',
  },
  {
    id: 'lab',
    label: 'Analytics Lab',
    description: 'Config performance & AI testing',
  },
  {
    id: 'cache',
    label: 'Cache',
    description: 'API cache stats & management',
  },
  {
    id: 'ai-accuracy',
    label: 'AI Accuracy',
    description: 'Calibration monitoring & performance tracking',
  },
  {
    id: 'direction-priors',
    label: 'Direction Priors',
    description: 'Empirical league × prop × direction offsets',
  },
  {
    id: 'picks-data',
    label: 'Picks Data',
    description: 'Browse and analyze all AI-generated picks',
  },
  {
    id: 'injury-comparison',
    label: 'Injury Comparison',
    description: 'BDL vs ESPN vs MSF side-by-side',
  },
];

// =============================================================================
// Component
// =============================================================================

export function AdminView(): ReactElement {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [picksDataFilters, setPicksDataFilters] = useState<PicksFilters>({});
  const tabBarRef = useRef<HTMLElement>(null);

  // A cross-tab link opens Picks Data narrowed; the tab bar opens it with no filter.
  const openTab = (tab: AdminTab, picksFilters: PicksFilters = {}): void => {
    setPicksDataFilters(picksFilters);
    setActiveTab(tab);
  };

  // On a phone the bar scrolls sideways, so a tab opened by a link can start out of view
  useEffect(() => {
    tabBarRef.current
      ?.querySelector(`[data-testid="admin-tab-${activeTab}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [activeTab]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-bold text-theme-text">Admin Panel</h1>
        <p className="text-theme-text-secondary">System management and monitoring tools</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-theme-border">
        <nav ref={tabBarRef} className="flex gap-6 overflow-x-auto">
          {ADMIN_TABS.map((tab) => (
            <button
              type="button"
              key={tab.id}
              data-testid={`admin-tab-${tab.id}`}
              data-active={activeTab === tab.id}
              onClick={() => openTab(tab.id)}
              className={`pb-3 px-1 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-theme-primary text-theme-primary font-medium'
                  : 'border-transparent text-theme-text-secondary hover:text-theme-text hover:border-theme-border'
              }`}
            >
              <div className="flex flex-col items-center text-center">
                <span className="text-sm font-medium">{tab.label}</span>
                <span className="text-xs text-theme-text-tertiary">{tab.description}</span>
              </div>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}

      {activeTab === 'users' && <UserManagementTab />}

      {activeTab === 'payouts' && (
        <div className="bg-theme-surface rounded-lg p-6 border border-theme-border">
          <PayoutsTab />
        </div>
      )}

      {activeTab === 'disputes' && (
        <div className="bg-theme-surface rounded-lg p-6 border border-theme-border">
          <DisputesTab />
        </div>
      )}

      {activeTab === 'support' && <SupportTab />}

      {activeTab === 'ai-accuracy' && (
        <AIAccuracyTab onOpenPicksData={(filters) => openTab('picks-data', filters)} />
      )}

      {activeTab === 'direction-priors' && <DirectionPriorsTab />}

      {activeTab === 'picks-data' && (
        <PicksDataTab
          initialFilters={picksDataFilters}
          onOpenAiAccuracy={() => openTab('ai-accuracy')}
        />
      )}

      {activeTab === 'injury-comparison' && <InjuryComparisonTab />}

      {activeTab === 'lab' && (
        <div className="bg-theme-surface rounded-lg p-6 border border-theme-border overflow-hidden">
          <LabView />
        </div>
      )}

      {activeTab === 'cache' && <CacheTab />}
    </div>
  );
}
